// =======================================================
// Pre-Matrícula e Ingresos View (Control de Admisiones)
// Piel "dnr-proposal" — ver docs/desarrollo/INTERFAZ.md antes de tocar el
// markup/CSS. Mismo patrón de cabecera compacta (título + badge de curso +
// chips de resumen + acciones en una sola fila) que Programación/Nóminas.
// =======================================================

const PreMatriculaView = {
    preRegistrations: [],
    // Diseño «A2» (2026-10-23): cifras arriba y, debajo, una sola línea de filtros —
    // vistas rápidas (Todos, Por aprobar, Pago pendiente, Confirmados, Fuera de plazo),
    // buscador y desplegables de estado, pago y grupo— sobre las tarjetas de grupo.
    view: 'todos',
    search: '',
    filterStatus: '',
    filterPay: '',
    filterGroup: '',
    // Tarjetas de grupo plegadas, por nombre de grupo (no por curso lectivo).
    collapsedGroups: new Set(),
    // Curso lectivo actualmente visible — null hasta el primer render(),
    // que lo inicializa al curso de hoy (mismo criterio que ProgramView).
    currentYearStart: null,

    // Mismos estados que ya limpian el badge "Previsión" al confirmarse (ver
    // PUT /api/pre-registration/:id en server.js) — un alumno en alguno de
    // estos ya tiene un compromiso de pago real, no solo pendiente. Se usa
    // para la cifra «Confirmados» y su vista rápida.
    CONFIRMED_STATUSES: ['matriculado', 'pago_aplazado', 'fe_en_curso', 'factura_hecha'],

    VIEWS: [['todos', 'Todos'], ['online', 'Por aprobar'], ['pendiente', 'Pago pendiente'], ['confirmados', 'Confirmados'], ['fuera', 'Fuera de plazo']],

    // Exact color codes matching the Excel legends
    // Las CLAVES (izquierda) no se tocan aunque cambien las etiquetas visibles
    // — 'matriculado' y 'pago_aplazado' siguen disparando lógica especial en
    // changeStatus() (aprobar matrícula + crear cuenta de usuario; marcar el
    // Enrollment de la BD como 'payment') y en server.js (mapeo inverso BD→
    // pre-registro combinado), así que renombrar la clave habría roto ambas
    // cosas sin necesidad. Solo se cambió el texto que ve el usuario:
    // 'matriculado' ahora se llama "Pagado" (sigue siendo "ya está resuelto,
    // alumno matriculado") y 'pago_aplazado' ahora se llama "F en curso"
    // (sigue siendo "pago diferido/en curso"). 'fe_en_curso' es clave nueva,
    // mapeada igual que 'pago_aplazado' en changeStatus() para las
    // solicitudes online (la BD no distingue entre planes F/FE).
    statuses: {
        'pendiente': { label: 'Pendiente', color: '#64748b', bg: '#f1f5f9' }, // Neutral Gray
        'entrevista_solicitada': { label: 'Entrev. solic.', color: '#6d28d9', bg: '#ede9fe' }, // Cool Lavender/Violet (abreviado para encajar sin desbordar)
        'entrevista_superada': { label: 'Entrev. super.', color: '#581c87', bg: '#d8b4fe' }, // Medium-Dark Purple
        'doc_pago': { label: 'Doc pago', color: '#034574', bg: '#a3d8fc' }, // Soft Sky Blue (#a3d8fc)
        'pago_aplazado': { label: 'F en curso', color: '#a16207', bg: '#fef9c3' }, // Yellow
        'fe_en_curso': { label: 'FE en curso', color: '#a16207', bg: '#fef9c3' }, // Yellow
        'matriculado': { label: 'Pagado', color: '#065f46', bg: '#85feaf' }, // Bright Mint Green (#85feaf)
        'factura_hecha': { label: 'Factura hecha', color: '#831843', bg: '#fe7ac5' }, // Vibrant Rose Pink (#fe7ac5)
        'extranjero': { label: 'Extranjero', color: '#1d4ed8', bg: '#dbeafe' }, // Blue
        'pagado_visado_pendiente': { label: 'Pag. visado pend.', color: '#0369a1', bg: '#e0f2fe' }, // Light Blue
        'becado': { label: 'Becado', color: '#a16207', bg: '#fef9c3' }, // Yellow
        'no_continua': { label: 'No continúa', color: '#b91c1c', bg: '#fee2e2' }, // Red
        // Va al final a propósito (orden del <select> = orden de este objeto):
        // el usuario la quiere "abajo de todo". No borra al alumno ni sus datos
        // (courseName/formationId se quedan intactos) — solo lo saca de su
        // tarjeta de grupo y de los totales de dinero (ver EXCLUDED_FROM_MONEY_STATUSES
        // y el filtro de `groups` en render()) y lo mueve a la pestaña "Fuera de
        // plazo", que lista a TODOS los de este curso lectivo sin importar su
        // grupo. Si más adelante se le cambia el estado a cualquier otro, vuelve
        // solo con eso a aparecer de nuevo en su tarjeta de grupo de siempre,
        // como alumno normal — no hace falta ninguna acción extra.
        'fuera_de_plazo': { label: 'Fuera de plazo', color: '#c2410c', bg: '#ffedd5' } // Orange
    },

    // Estados que no deben sumar a "Previsión ingresos"/"Cobrado"/"Pendiente"
    // (ni a los mismos totales por tarjeta de grupo) — 'no_continua' ya se
    // excluía porque el alumno se cae del proceso; 'fuera_de_plazo' se suma
    // aquí porque, aunque sigue "vivo" y puede volver, mientras esté fuera de
    // plazo no se cuenta como ingreso previsto real.
    EXCLUDED_FROM_MONEY_STATUSES: ['no_continua', 'fuera_de_plazo'],

    setView(view) {
        if (!this.VIEWS.some(([id]) => id === view)) return;
        this.view = view;
        this.render();
    },

    setFilter(key, value) {
        if (!['filterStatus', 'filterPay', 'filterGroup'].includes(key)) return;
        this[key] = value || '';
        this.render();
    },

    // The search only repaints the groups, so the field keeps its focus while typing.
    setSearch(value) {
        this.search = String(value || '');
        this.refreshGroups();
    },

    toggleGroup(groupName) {
        if (this.collapsedGroups.has(groupName)) this.collapsedGroups.delete(groupName);
        else this.collapsedGroups.add(groupName);
        this.refreshGroups();
    },

    refreshGroups() {
        const box = document.getElementById('pmv-groups');
        if (box) SafeHTML.set(box, this.renderGroups(this.yearRegistrations()));
    },

    // Curso lectivo (Sep-Ago) de hoy — mismo criterio que en el resto de la
    // app (Programación, Nóminas, stats del dashboard).
    todayAcademicYearStart() {
        const now = new Date();
        return now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
    },

    // Cursos lectivos que ofrecer en el selector: todos los que tengan al
    // menos un registro, más el curso de hoy como ancla aunque no haya
    // datos todavía (mismo patrón que ProgramView.getAcademicYearStarts).
    getAvailableYears() {
        const years = new Set([this.todayAcademicYearStart()]);
        this.preRegistrations.forEach(r => {
            if (typeof r.academicYearStart === 'number') years.add(r.academicYearStart);
        });
        return [...years].sort((a, b) => b - a);
    },

    // Grupos de Pre-Matrícula («Agrupar formaciones»): desde un curso lectivo,
    // las formaciones de varios planes y cursos cuentan como un solo grupo
    // económico. Académicamente nada cambia. Mismo criterio que groupFor() en
    // server/security/pre-registration-groups.js.
    sharedGroups: [],

    sharedGroupFor(formation) {
        const matches = this.sharedGroups.filter(group => this.currentYearStart >= group.fromYear && (group.members || []).some(member =>
            member.studyPlanId === formation.studyPlanId && String(member.academicYear) === String(formation.academicYear)));
        return matches.sort((a, b) => b.fromYear - a.fromYear)[0] || null;
    },

    // Nombre de grupo económico para una formación en el curso visible: el de su
    // grupo de Pre-Matrícula si lo tiene; si no, su nombre real.
    prematriculaGroupName(formation) {
        return this.sharedGroupFor(formation)?.name || formation.name;
    },

    // Ordena las pestañas de curso siguiendo el mismo orden del catálogo de
    // Formaciones (Formation.order — diplomaturas/máster/curso/taller/intensivo,
    // y dentro de diplomaturas cada itinerario completo) en vez del orden de
    // aparición en los registros. "SIN ASIGNAR" y cualquier nombre que no
    // coincida con ninguna formación real van siempre al final.
    sortGroupNamesByCatalog(groupNames) {
        const orderByName = {};
        if (typeof FormationService !== 'undefined') {
            FormationService.getFormations().forEach(f => {
                const order = f.order || 0;
                // Un grupo de Pre-Matrícula representa a varias formaciones a la
                // vez: le corresponde la posición más temprana de todas ellas.
                const key = this.prematriculaGroupName(f).toUpperCase();
                if (orderByName[key] === undefined || order < orderByName[key]) {
                    orderByName[key] = order;
                }
            });
        }
        return [...groupNames].sort((a, b) => {
            const oa = orderByName[a] !== undefined ? orderByName[a] : Infinity;
            const ob = orderByName[b] !== undefined ? orderByName[b] : Infinity;
            if (oa !== ob) return oa - ob;
            return a.localeCompare(b);
        });
    },

    setAcademicYear(yearStart) {
        const y = parseInt(yearStart, 10);
        if (Number.isNaN(y)) return;
        this.currentYearStart = y;
        this.render();
    },

    async saveInterviewDateTime(id, date, time) {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        if (id.startsWith('db_')) { SafeHTML.fire('Aprueba primero la solicitud', 'Hasta aprobarla (✓) no se pueden programar entrevistas, abrir el pago ni cambiar su estado.', 'info'); return; }
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/pre-registration/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify({
                    interviewDate: date,
                    interviewTime: time
                })
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.error);
            if (data.emailSent) {
                Utils.showToast('Entrevista programada y citación enviada por email al alumno', 'success');
            } else {
                Utils.showToast('Entrevista programada correctamente', 'success');
            }
            await this.loadData();
            this.render();
        } catch(e) {
            console.error(e);
            SafeHTML.fire('Error', 'No se pudo guardar la fecha de la entrevista', 'error');
        }
    },

    async init() {
        this.preRegistrations = [];
        await this.loadData();
        this.render();
    },

    async loadData() {
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/pre-registration', {
                headers: { 'X-SICAF-CSRF': token }
            });
            const data = await res.json();
            if (data.success) {
                this.preRegistrations = data.preRegistrations || [];
            }
            const groups = await fetch('/api/pre-registration-groups', {headers: {'X-SICAF-CSRF': token}, cache: 'no-store'})
                .then(r => r.json()).catch(() => null);
            if (Array.isArray(groups?.groups)) this.sharedGroups = groups.groups;
        } catch (e) {
            console.error('Error loading pre-registration data:', e);
        }
    },

    async changeStatus(id, newStatus) {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        // Solicitud online sin aprobar: nada (entrevistas, pago, estados) hasta aprobarla
        // con ✓ — el servidor lo rechaza igualmente. Aprobar/rechazar van por sus botones.
        if (id.startsWith('db_')) {
            SafeHTML.fire('Aprueba primero la solicitud', 'Hasta aprobarla (✓) no se pueden programar entrevistas, abrir el pago ni cambiar su estado.', 'info');
            this.render();
            return;
        }

        // Al marcar como "Pagado" se salda automáticamente lo pendiente —
        // no tiene sentido que un alumno quede en "Pagado" con importe
        // pendiente todavía a mano en la ficha.
        const body = { status: newStatus };
        if (newStatus === 'matriculado') {
            const record = this.preRegistrations.find(r => r.id === id);
            if (record) {
                body.amountPaid = record.coursePrice;
            }
        }

        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/pre-registration/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (data.success) {
                // Confirmar el pago (estado -> matriculado) de un alumno ya vinculado a
                // Alumnado puede haber disparado la creación de su cuenta de acceso al
                // portal (ver createStudentPortalAccount en server.js) — si es así, se
                // avisa con un aviso propio en vez del toast breve habitual, porque es
                // un evento que solo pasa una vez y conviene que no se pierda de vista.
                if (data.username) {
                    SafeHTML.fire('¡Pagado!', `Estado actualizado a Pagado y cuenta de acceso creada.<br>Usuario: <b>${Utils.sanitizeHTML(data.username)}</b>`, 'success');
                } else if (data.paymentSectionEmailSent) {
                    Utils.showToast('Estado actualizado. Se ha enviado al alumno el enlace para subir la documentación de pago.', 'success');
                } else if (newStatus === 'entrevista_superada') {
                    Utils.showToast('Entrevista superada. Notificación enviada a Administración y RRHH.', 'success');
                } else {
                    Utils.showToast(body.amountPaid !== undefined ? 'Estado actualizado a Pagado y pendiente saldado' : 'Estado actualizado', 'success');
                }
                await this.loadData();
                this.render();
            }
        } catch (e) {
            console.error('Error updating status:', e);
            SafeHTML.fire('Error', 'No se pudo actualizar el estado', 'error');
        }
    },

    // Aprobar/rechazar una solicitud online directamente desde su fila en
    // Prematrícula — antes había que ir a Alumnado > Solicitudes de Admisión
    // para esto. Mismos endpoints que usa StudentView.approveAdmission()/
    // rejectAdmission() (sin markAsPaid: aprobar aquí solo registra al alumno,
    // igual que "Aprobar Matrícula" en Alumnado — confirmar el pago es una
    // acción aparte, vía el desplegable de Estado -> "Pagado").
    // Datos de la solicitud online para StudentView.resolveEnrollmentLink().
    linkApplicant(dbId, name) {
        const row = this.preRegistrations.find(r => r.id === 'db_' + dbId) || {};
        return { requiresLinkReview: row.requiresLinkReview, dni: row.dni, email: row.email,
            name: `${row.firstName || ''} ${row.lastName1 || ''}`.trim() || name };
    },

    async approveOnlineRequest(dbId, name) {
        const result = await SafeHTML.fire({
            title: '¿Aprobar solicitud de matrícula?',
            html: `Se procederá a registrar al alumno <b>${Utils.sanitizeHTML(name)}</b> en el centro. Su cuenta de acceso al portal se creará automáticamente cuando se confirme su pago.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, aprobar matrícula',
            cancelButtonText: 'Cancelar'
        });
        if (!result.isConfirmed) return;
        const link = await StudentView.resolveEnrollmentLink(dbId, this.linkApplicant(dbId, name));
        if (!link) return;

        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch(`/api/enrollments/approve/${dbId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': token },
                body: JSON.stringify(link)
            });
            const res = await response.json();
            if (res.success) {
                SafeHTML.fire('Matrícula Aprobada', 'El estudiante se ha registrado correctamente en el centro.', 'success');
                await this.loadData();
                this.render();
            } else {
                throw new Error(res.error || 'Server error');
            }
        } catch (err) {
            SafeHTML.fire('Error', err.message || 'No se pudo completar la aprobación.', 'error');
        }
    },

    async rejectOnlineRequest(dbId, name) {
        const result = await SafeHTML.fire({
            title: '¿Rechazar solicitud de matrícula?',
            html: `La solicitud de <b>${Utils.sanitizeHTML(name)}</b> se moverá a "Rechazados" en Alumnado, desde donde podrá consultarse o reabrirse.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, rechazar',
            cancelButtonText: 'Cancelar'
        });
        if (!result.isConfirmed) return;

        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch(`/api/enrollments/reject/${dbId}`, {
                method: 'POST',
                headers: { 'X-SICAF-CSRF': token }
            });
            const res = await response.json();
            if (res.success) {
                SafeHTML.fire('Matrícula Rechazada', 'La solicitud ha sido descartada.', 'success');
                await this.loadData();
                this.render();
            } else {
                throw new Error(res.error || 'Server error');
            }
        } catch (err) {
            SafeHTML.fire('Error', err.message || 'No se pudo rechazar la solicitud.', 'error');
        }
    },

    // Cambio de forma de pago directamente desde la columna "Pago" de la tabla,
    // sin pasar por el modal de Editar — mismo patrón que el desplegable de
    // "Estado" (changeStatus), pero sin confirmación previa porque no dispara
    // ningún efecto secundario (crear cuenta, notificar, etc.), solo cambia el
    // dato. No aplica a solicitudes online (id "db_...") — esas no tienen un
    // registro propio en Prematrícula todavía, se editan desde Alumnado.
    async changePaymentOption(id, newPaymentOption) {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/pre-registration/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify({ paymentOption: newPaymentOption })
            });
            const data = await res.json();
            if (data.success) {
                Utils.showToast('Forma de pago actualizada', 'success');
                await this.loadData();
                this.render();
            } else {
                throw new Error(data.error || 'Error del servidor');
            }
        } catch (e) {
            console.error('Error updating payment option:', e);
            SafeHTML.fire('Error', 'No se pudo actualizar la forma de pago', 'error');
            this.render();
        }
    },

    // Crear el acceso al portal (usuario + contraseña temporal, con email de
    // bienvenida) sin necesidad de marcar el estado como "Pagado" — hasta ahora
    // esa era la única vía (ver 'matriculado' en changeStatus()), pero un
    // alumno en estado "F en curso" / "FE en curso" (pago_aplazado/fe_en_curso
    // — financiación en curso) puede necesitar acceso al portal antes de
    // terminar de pagar, sin que eso implique confirmar el pago completo. Ojo:
    // no confundir con el campo "Pago" (C/F/FE, forma de pago elegida) — el
    // botón se dispara por el ESTADO, no por la forma de pago (bug real
    // reportado al probar la primera versión: "he seleccionado f y fe no me
    // sale" — el usuario había cambiado el Estado a "F en curso"/"FE en curso",
    // pero el botón estaba enganchado al campo Pago). Solo aparece el botón
    // para registros ya vinculados a un alumno real (studentId) — sin eso no
    // hay a quién crear la cuenta, igual que la creación automática al marcar
    // "Pagado". Idempotente en el servidor: si ya existía cuenta, avisa en vez
    // de generar credenciales nuevas.
    async createPortalAccount(id, name) {
        const result = await SafeHTML.fire({
            title: '¿Crear acceso al portal?',
            html: `Se generará un usuario y contraseña temporal para <b>${Utils.sanitizeHTML(name)}</b> y se le enviará por email — sin necesidad de marcarlo como "Pagado".`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#0d9488',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, crear acceso',
            cancelButtonText: 'Cancelar'
        });
        if (!result.isConfirmed) return;

        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch(`/api/pre-registration/${id}/create-account`, {
                method: 'POST',
                headers: { 'X-SICAF-CSRF': token }
            });
            const res = await response.json();
            if (!res.success) throw new Error(res.error || 'Error del servidor');

            if (res.alreadyExisted) {
                SafeHTML.fire('Ya tenía acceso', `Este alumno ya tenía una cuenta creada.<br>Usuario: <b>${Utils.sanitizeHTML(res.username)}</b>`, 'info');
            } else {
                SafeHTML.fire('¡Acceso creado!', res.username
                    ? `Cuenta creada y credenciales enviadas por email.<br>Usuario: <b>${Utils.sanitizeHTML(res.username)}</b>`
                    : 'Cuenta creada correctamente.', 'success');
            }

            // Recargar y repintar — `hasPortalAccount` (ver getCombinedPreRegistrations
            // en server.js) ya vendrá a `true` para este registro, así que el botón
            // llave desaparece solo de esta fila sin tener que quitarlo a mano aquí.
            await this.loadData();
            this.render();
        } catch (err) {
            SafeHTML.fire('Error', err.message || 'No se pudo crear el acceso al portal.', 'error');
        }
    },

    // Fila de alumno de las tarjetas de grupo. `groupName` es el grupo de la tarjeta:
    // se usa solo para decidir si hace falta la píldora de especialidad.
    renderStudentRow(s, groupName) {
        const config = this.statuses[s.status] || { label: s.status, color: '#475569', bg: '#f1f5f9' };
        const isCancelled = s.status === 'no_continua' || s.status === 'fuera_de_plazo';

        // Cuando el grupo económico no es el nombre real de la formación (un grupo
        // de Pre-Matrícula junta varias) hay que poder ver igualmente qué especialidad
        // eligió cada alumno — se resuelve la Formation real por formationId y,
        // si su nombre no coincide con el del grupo, se pinta como píldora de una
        // letra junto al nombre, con la letra y el color de su plan de estudios.
        const trackFormation = (s.formationId && typeof FormationService !== 'undefined')
            ? FormationService.getFormation(s.formationId) : null;
        // Letra y color: los del plan de estudios (Plan de Estudios → Planes).
        const showTrackPill = trackFormation && trackFormation.name.toUpperCase() !== groupName;
        const trackPlan = showTrackPill && typeof StudyPlanService !== 'undefined' ? StudyPlanService.getPlan(trackFormation.studyPlanId) : null;
        const trackColor = showTrackPill ? (/^#[0-9a-f]{6}$/i.test(trackPlan?.color || '') ? trackPlan.color : '#475569') : null;
        const trackInitial = showTrackPill ? Utils.sanitizeHTML(trackPlan?.initials || (trackPlan?.name || trackFormation.name).charAt(0).toUpperCase() || '?') : null;
        const name = `${Utils.sanitizeHTML(s.firstName)} ${Utils.sanitizeHTML(s.lastName1)}`;

        return `
        <tr class="${isCancelled ? 'is-cancelled' : ''} ${s.isDb ? 'is-online' : ''}">
            <td class="pmv-name-cell">
                <div class="pmv-name-inner">
                    ${showTrackPill ? `<span class="pmv-track-pill" style="color:${trackColor}; border-color:${trackColor};" title="Especialidad elegida: ${Utils.sanitizeHTML(trackFormation.name)}">${trackInitial}</span>` : ''}
                    ${s.studentId ? `
                    <span class="pmv-name-text pmv-name-link" ${UIEvents.attr("click", function(event,captured){App.navigate(String(captured[0]));}, [Utils.studentProfileUrl({ id: s.studentId, firstName: s.firstName, lastName: s.lastName1 })])} title="Ver ficha del alumno">${name}</span>
                    ` : `
                    <span class="pmv-name-text" title="${name}${s.notes ? ' — ' + Utils.sanitizeHTML(s.notes) : ''}">${name}</span>
                    `}
                    ${s.isDb ? '<span class="pmv-tag is-online" title="Solicitud hecha desde la web: apruébala para poder cambiar su estado">Online</span>' : ''}
                    ${s.isProvisional ? `<span class="pmv-tag" title="Candidato a promocionar — pendiente de confirmar desde Alumnado">Previsión</span>` : ''}
                </div>
                <div class="pmv-name-sub">${s.amountPending > 0 && !isCancelled ? `<span class="pmv-pending">${Utils.formatCurrency(s.amountPending)} pend.</span>` : `<span class="pmv-pending-zero">${s.isDb ? 'Solicitud online' : 'Al día'}</span>`}</div>
            </td>
            <td class="ta-c">
                ${s.isDb ? `
                <span class="pmv-payment-pill" title="Las solicitudes online se editan desde Alumnado, no aquí">${Utils.sanitizeHTML(s.paymentOption)}</span>
                ` : `
                <select class="pmv-payment-select" aria-label="Forma de pago de ${name}" ${UIEvents.attr("change", function(event,captured){PreMatriculaView.changePaymentOption(String(captured[0]),this.value);}, [s.id])} title="Cambiar forma de pago">
                    <option value="C" ${s.paymentOption === 'C' ? 'selected' : ''}>C</option>
                    <option value="F" ${s.paymentOption === 'F' ? 'selected' : ''}>F</option>
                    <option value="FE" ${s.paymentOption === 'FE' ? 'selected' : ''}>FE</option>
                    ${!['C', 'F', 'FE'].includes(s.paymentOption) ? `<option value="${Utils.sanitizeHTML(s.paymentOption)}" selected>${Utils.sanitizeHTML(s.paymentOption)}</option>` : ''}
                </select>
                `}
            </td>
            <td class="ta-r pmv-price pmv-col-price">${Utils.formatCurrency(s.coursePrice)}</td>
            <td class="ta-r pmv-paid pmv-col-paid">${Utils.formatCurrency(s.amountPaid)}</td>
            <td class="ta-r pmv-col-pend ${s.amountPending > 0 && !isCancelled ? 'pmv-pending' : 'pmv-pending-zero'}">${Utils.formatCurrency(s.amountPending)}</td>
            <td class="pmv-status-cell">
                <select class="pmv-status-select" aria-label="Estado de ${name}" ${UIEvents.attr("change", function(event,captured){PreMatriculaView.changeStatus(String(captured[0]),this.value);}, [s.id])}
                        ${s.isDb ? 'disabled' : ''}
                        title="${s.isDb ? 'Aprueba primero la solicitud para cambiar su estado' : (this.statuses[s.status]?.label || s.status)}"
                        style="--pmv-status:${config.color}; --pmv-status-bg:${config.bg};">
                    ${Object.keys(this.statuses).map((statusKey) => {
                        const optHtml = `<option value="${statusKey}" ${s.status === statusKey ? 'selected' : ''}>${Utils.sanitizeHTML(this.statuses[statusKey].label)}</option>`;
                        if (statusKey === 'factura_hecha') {
                            return optHtml + `<option disabled>──────────</option>`;
                        }
                        return optHtml;
                    }).join('')}
                </select>
            </td>
            <td class="ta-r">
                <div class="pmv-row-actions">
                    ${s.isDb ? `
                        <button type="button" class="pmv-btn is-small is-primary" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.approveOnlineRequest(String(captured[0]),String(captured[1])+" "+String(captured[2]));}, [s.id.replace('db_', ''), s.firstName, s.lastName1])}>Aprobar</button>
                        <button type="button" class="pmv-btn is-small" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.rejectOnlineRequest(String(captured[0]),String(captured[1])+" "+String(captured[2]));}, [s.id.replace('db_', ''), s.firstName, s.lastName1])}>Rechazar</button>
                    ` : `
                        ${(s.studentId && !s.hasPortalAccount && ['pago_aplazado', 'fe_en_curso'].includes(s.status)) ? `
                        <button type="button" class="btn-icon" aria-label="Dar acceso al portal a ${name}" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.createPortalAccount(String(captured[0]),String(captured[1])+" "+String(captured[2]));}, [s.id, s.firstName, s.lastName1])} title="Crear acceso al portal y enviar credenciales (${Utils.sanitizeHTML(this.statuses[s.status].label)}) — no hace falta esperar a marcarlo como Pagado"><i class="fas fa-key" aria-hidden="true"></i></button>
                        ` : ''}
                        <button type="button" class="btn-icon" aria-label="Editar a ${name}" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.showEditStudentModal(String(captured[0]));}, [s.id])} title="Editar"><i class="fas fa-pen" aria-hidden="true"></i></button>
                        <button type="button" class="btn-icon is-danger" aria-label="Eliminar a ${name}" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.deleteStudent(String(captured[0]),String(captured[1])+" "+String(captured[2]));}, [s.id, s.firstName, s.lastName1])} title="Eliminar"><i class="fas fa-trash-alt" aria-hidden="true"></i></button>
                    `}
                </div>
            </td>
        </tr>`;
    },

    /** The registrations of the course shown. */
    yearRegistrations() {
        if (this.currentYearStart === null) this.currentYearStart = this.todayAcademicYearStart();
        return this.preRegistrations.filter(r => r.academicYearStart === this.currentYearStart);
    },

    /** Whether a registration is in a quick view (before the search and the drop-downs). */
    inView(r, view = this.view) {
        if (view === 'online') return !!r.isDb;
        if (view === 'fuera') return r.status === 'fuera_de_plazo';
        if (r.status === 'fuera_de_plazo') return false;
        if (view === 'pendiente') return !this.EXCLUDED_FROM_MONEY_STATUSES.includes(r.status) && r.amountPending > 0;
        if (view === 'confirmados') return this.CONFIRMED_STATUSES.includes(r.status);
        return true;
    },

    matchesFilters(r) {
        const term = Utils.normalizeString ? Utils.normalizeString(this.search.trim()) : this.search.trim().toLowerCase();
        const name = [r.firstName, r.lastName1, r.lastName2].filter(Boolean).join(' ');
        return (!term || (Utils.normalizeString ? Utils.normalizeString(name) : name.toLowerCase()).includes(term))
            && (!this.filterStatus || r.status === this.filterStatus)
            && (!this.filterPay || r.paymentOption === this.filterPay)
            && (!this.filterGroup || r.courseName === this.filterGroup);
    },

    /** Every group of the course: its registrations (the late ones too) and the open formations without anyone yet. */
    groupsOf(registrations) {
        const groups = {};
        registrations.forEach(item => {
            if (!groups[item.courseName]) groups[item.courseName] = [];
            groups[item.courseName].push(item);
        });
        // Además de los grupos que ya tienen registros, seguir mostrando una tarjeta
        // vacía por cada formación real abierta a matrícula o con alumnado activo en
        // Alumnado — una formación recién abierta a matrícula (sin nadie apuntado
        // todavía) también tiene que aparecer.
        if (typeof FormationService !== 'undefined' && typeof StudentService !== 'undefined') {
            const activeFormationIds = new Set(
                StudentService.getStudents().filter(s => s.active).flatMap(s => s.formations || [])
            );
            FormationService.getFormations().forEach(f => {
                if (f.enrollmentOpen || activeFormationIds.has(f.id)) {
                    const key = this.prematriculaGroupName(f).toUpperCase();
                    if (!groups[key]) groups[key] = [];
                }
            });
        }
        return groups;
    },

    /** Price, paid and pending of the registrations that count as income. */
    moneyOf(registrations) {
        return registrations.filter(r => !this.EXCLUDED_FROM_MONEY_STATUSES.includes(r.status))
            .reduce((t, r) => ({ total: t.total + r.coursePrice, paid: t.paid + r.amountPaid, pending: t.pending + r.amountPending, count: t.count + 1,
                confirmed: t.confirmed + (this.CONFIRMED_STATUSES.includes(r.status) ? 1 : 0) }), { total: 0, paid: 0, pending: 0, count: 0, confirmed: 0 });
    },

    renderGroups(registrations) {
        const groups = this.groupsOf(registrations);
        const filtering = this.view !== 'todos' || this.search.trim() || this.filterStatus || this.filterPay || this.filterGroup;
        const cards = this.sortGroupNamesByCatalog(Object.keys(groups)).map(groupName => {
            const all = groups[groupName];
            const rows = all.filter(r => this.inView(r) && this.matchesFilters(r));
            // Filtering: only the groups with someone to show. Without filters every group stays, the empty ones too.
            if (filtering && !rows.length) return '';
            if (this.filterGroup && groupName !== this.filterGroup) return '';
            const money = this.moneyOf(all);
            const pct = money.total > 0 ? Math.min(100, Math.round(money.paid / money.total * 100)) : 0;
            const open = !this.collapsedGroups.has(groupName);
            return `
            <section class="pmv-group-card">
                <div class="pmv-group-header">
                    <div class="pmv-group-title">
                        <button type="button" class="pmv-fold" aria-expanded="${open}" aria-label="${open ? 'Plegar' : 'Desplegar'} ${Utils.sanitizeHTML(groupName)}"
                                ${UIEvents.attr("click", function(event,captured){PreMatriculaView.toggleGroup(String(captured[0]));}, [groupName])}>
                            <i class="fas fa-chevron-${open ? 'down' : 'right'}" aria-hidden="true"></i>
                        </button>
                        <div>
                            <h2>${Utils.sanitizeHTML(groupName)}</h2>
                            <div class="pmv-group-sub">${all.filter(r => r.status !== 'fuera_de_plazo').length} inscritos · ${money.confirmed} confirmados</div>
                        </div>
                    </div>
                    <div class="pmv-group-money">
                        <div class="pmv-group-money-line"><span><strong>${Utils.formatCurrency(money.paid)}</strong> cobrado de ${Utils.formatCurrency(money.total)}</span><span class="is-warn">${Utils.formatCurrency(money.pending)} pend.</span></div>
                        <div class="pmv-bar" role="img" aria-label="${pct} % cobrado"><span style="width:${pct}%"></span></div>
                    </div>
                </div>
                ${open ? (rows.length ? `
                <div class="pmv-table-wrap">
                    <table class="pmv-table">
                        <thead>
                            <tr>
                                <th scope="col">Alumno</th>
                                <th scope="col" class="ta-c">Pago</th>
                                <th scope="col" class="ta-r pmv-col-price">Precio</th>
                                <th scope="col" class="ta-r pmv-col-paid">Pagado</th>
                                <th scope="col" class="ta-r pmv-col-pend">Pend.</th>
                                <th scope="col">Estado</th>
                                <th scope="col"><span class="sr-only">Acciones</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rows.map(s => this.renderStudentRow(s, groupName)).join('')}
                        </tbody>
                    </table>
                </div>` : '<p class="pmv-group-empty">Todavía no hay nadie en este grupo.</p>') : ''}
            </section>`;
        }).filter(Boolean);
        if (cards.length) return cards.join('');
        const view = this.VIEWS.find(([id]) => id === this.view)[1];
        return `
            <div class="pmv-empty">
                <i class="fas fa-users" aria-hidden="true"></i>
                <h3>${filtering ? `Nadie en «${Utils.sanitizeHTML(view)}» con estos filtros` : `No hay alumnos en el curso ${this.currentYearStart} / ${this.currentYearStart + 1}`}</h3>
                <p>${filtering ? 'Prueba con otra vista o quita algún filtro.' : 'Crea un grupo nuevo para empezar a pre-matricular alumnos, o elige otro curso lectivo arriba.'}</p>
                ${filtering ? '' : `<button type="button" class="btn btn-primary" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.showAddStudentModal();}, [])}>Añadir alumno</button>`}
            </div>`;
    },

    render() {
        const container = document.getElementById('content-area');
        if (!container) return;

        // Solo el curso lectivo seleccionado — el resto de cálculos y
        // agrupaciones de aquí en adelante parten de esta lista filtrada.
        const yearRegistrations = this.yearRegistrations();
        // «No continúa» y «Fuera de plazo» no suman a los totales de dinero.
        const money = this.moneyOf(yearRegistrations);
        const collectedPct = money.total > 0 ? Math.round((money.paid / money.total) * 100) : 0;
        const years = this.getAvailableYears();
        const counts = Object.fromEntries(this.VIEWS.map(([id]) => [id, yearRegistrations.filter(r => this.inView(r, id)).length]));
        const groupNames = this.sortGroupNamesByCatalog(Object.keys(this.groupsOf(yearRegistrations)));
        const more = (title, text, fn) => `<button type="button" role="menuitem" class="pmv-menu-item" ${UIEvents.attr("click", fn, [])}><strong>${title}</strong><span>${text}</span></button>`;
        const select = (key, label, options) => `
            <label class="sr-only" for="pmv-${key}">${label}</label>
            <select id="pmv-${key}" class="pmv-filter-select" ${UIEvents.attr("change", function(event,captured){PreMatriculaView.setFilter(String(captured[0]),this.value);}, [key])}>
                <option value="">${label}</option>
                ${options.map(([value, text]) => `<option value="${Utils.sanitizeHTML(value)}" ${this[key] === value ? 'selected' : ''}>${Utils.sanitizeHTML(text)}</option>`).join('')}
            </select>`;

        let html = `
        <div class="pmv-container dnr-proposal">
            <div class="pmv-head">
                <div class="pmv-head-left">
                    <h1 class="pmv-title">Pre-Matrícula e ingresos</h1>
                    <label class="sr-only" for="pmv-year-select">Curso lectivo</label>
                    <select id="pmv-year-select" class="program-year-badge" ${UIEvents.attr("change", function(event,captured){PreMatriculaView.setAcademicYear(this.value);}, [])} title="Curso lectivo">
                        ${years.map(y => `<option value="${y}" ${y === this.currentYearStart ? 'selected' : ''}>CURSO ${y} / ${y + 1}</option>`).join('')}
                    </select>
                </div>
                <div class="pmv-top-actions">
                    <button type="button" class="pmv-btn is-primary" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.showAddStudentModal();}, [])}>
                        <i class="fas fa-plus" aria-hidden="true"></i> Añadir alumno
                    </button>
                    <details class="pmv-more">
                        <summary class="pmv-btn">Más <i class="fas fa-chevron-down" aria-hidden="true"></i></summary>
                        <div class="pmv-menu" role="menu">
                            ${more('Generar previsión', 'Los alumnos activos de este curso, como candidatos al curso elegido', function(event,captured){this.closest('details').open=false;PreMatriculaView.generatePromotionForecast();})}
                            ${more('Crear grupo', 'Una tarjeta nueva para pre-matricular', function(event,captured){this.closest('details').open=false;PreMatriculaView.showAddGroupModal();})}
                            ${more('Agrupar formaciones', 'Varias formaciones en un solo grupo económico desde un curso', function(event,captured){this.closest('details').open=false;PreMatriculaView.showSharedGroups();})}
                        </div>
                    </details>
                </div>
            </div>

            <div class="pmv-kpis">
                <div class="pmv-kpi is-dark">
                    <div class="pmv-kpi-label">Cobrado</div>
                    <div class="pmv-kpi-line"><span class="pmv-kpi-value">${Utils.formatCurrency(money.paid)}</span><span class="pmv-kpi-of">de ${Utils.formatCurrency(money.total)} · ${collectedPct} %</span></div>
                    <div class="pmv-bar is-dark" role="img" aria-label="${collectedPct} % cobrado"><span style="width:${Math.min(100, collectedPct)}%"></span></div>
                </div>
                <div class="pmv-kpi">
                    <div class="pmv-kpi-label">Pendiente de cobro</div>
                    <div class="pmv-kpi-value is-warn">${Utils.formatCurrency(money.pending)}</div>
                    <div class="pmv-kpi-note">Sin contar «No continúa» ni «Fuera de plazo»</div>
                </div>
                <div class="pmv-kpi">
                    <div class="pmv-kpi-label">Confirmados</div>
                    <div class="pmv-kpi-value">${money.confirmed} / ${money.count}</div>
                    <div class="pmv-kpi-note">Pagado, F o FE en curso, factura hecha</div>
                </div>
            </div>

            <div class="pmv-filters">
                <label class="sr-only" for="pmv-search">Buscar alumno</label>
                <input type="search" id="pmv-search" class="pmv-search" placeholder="Buscar alumno…" value="${Utils.sanitizeHTML(this.search)}" autocomplete="off"
                       ${UIEvents.attr("input", function(event,captured){PreMatriculaView.setSearch(this.value);}, [])}>
                <div class="pmv-views" role="group" aria-label="Vista">
                    ${this.VIEWS.map(([id, label]) => `<button type="button" class="pmv-view ${this.view === id ? 'is-on' : ''}" aria-pressed="${this.view === id}"
                            ${UIEvents.attr("click", function(event,captured){PreMatriculaView.setView(String(captured[0]));}, [id])}>
                        ${label}${['online', 'fuera'].includes(id) && counts[id] ? `<span class="pmv-count ${id === 'online' ? 'is-warn' : ''}">${counts[id]}</span>` : ''}
                    </button>`).join('')}
                </div>
                <span class="pmv-filters-gap" aria-hidden="true"></span>
                ${select('filterStatus', 'Estado', Object.entries(this.statuses).map(([key, s]) => [key, s.label]))}
                ${select('filterPay', 'Pago', [['C', 'C'], ['F', 'F'], ['FE', 'FE']])}
                ${select('filterGroup', 'Grupo', groupNames.map(name => [name, name]))}
            </div>

            <div class="pmv-groups" id="pmv-groups">
                ${this.renderGroups(yearRegistrations)}
            </div>
        </div>
        `;

        SafeHTML.set(container, html);
        // App.navigate() llama a `content = view.render()` y luego hace él mismo
        // `contentArea.innerHTML = content` — sin este `return`, `content` era
        // `undefined` y esa segunda asignación sobrescribía el HTML recién
        // pintado arriba con el texto literal "undefined", visible unos segundos
        // hasta que `init()` termina de cargar datos y vuelve a renderizar.
        return html;
    },

    // Opciones del <select> "Curso lectivo" de los modales de alta/edición —
    // todos los cursos con datos más el curso de hoy, como en el selector
    // de cabecera.
    renderYearOptionsForForm(selectedYear) {
        return this.getAvailableYears().map(y =>
            `<option value="${y}" ${y === selectedYear ? 'selected' : ''}>Curso ${y} / ${y + 1}</option>`
        ).join('');
    },

    showAddStudentModal() {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        const yearRegistrations = this.preRegistrations.filter(r => r.academicYearStart === this.currentYearStart);
        const groups = [...new Set(yearRegistrations.map(r => r.courseName))];

        const modalHtml = `
            <div class="dnr-proposal pmv-form-dnr">
                <form id="add-pre-student-form" ${UIEvents.attr("submit", function(event,captured){PreMatriculaView.handleAddStudent(event);}, [])}>
                    <div class="form-group">
                        <label class="form-label">Curso / Grupo *</label>
                        <select name="courseName" class="form-input" required>
                            <option value="">-- Elige curso --</option>
                            ${groups.map(g => `<option value="${g}">${Utils.sanitizeHTML(g)}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Curso Lectivo *</label>
                        <select name="academicYearStart" class="form-input" required>
                            ${this.renderYearOptionsForForm(this.currentYearStart)}
                        </select>
                    </div>
                    <div class="pmv-form-row-3">
                        <div class="form-group">
                            <label class="form-label">Nombre *</label>
                            <input type="text" name="firstName" class="form-input" required>
                        </div>
                        <div class="form-group">
                            <label class="form-label">1º Apellido *</label>
                            <input type="text" name="lastName1" class="form-input" required>
                        </div>
                        <div class="form-group">
                            <label class="form-label">2º Apellido</label>
                            <input type="text" name="lastName2" class="form-input">
                        </div>
                    </div>
                    <div class="pmv-form-row-3">
                        <div class="form-group">
                            <label class="form-label">Opción Pago *</label>
                            <select name="paymentOption" class="form-input" required>
                                <option value="C">C</option>
                                <option value="F">F</option>
                                <option value="FE">FE</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label class="form-label">Precio Curso *</label>
                            <input type="number" step="0.01" name="coursePrice" value="5000.00" class="form-input" required>
                        </div>
                        <div class="form-group">
                            <label class="form-label">Importe Pagado</label>
                            <input type="number" step="0.01" name="amountPaid" value="0.00" class="form-input" required>
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Estado Proceso *</label>
                        <select name="status" class="form-input" required>
                            ${Object.keys(this.statuses).map(k => `<option value="${k}">${Utils.sanitizeHTML(this.statuses[k].label)}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Notas / Observaciones</label>
                        <textarea name="notes" class="form-input" rows="3" placeholder="Añadir notas..."></textarea>
                    </div>
                    <div class="pmv-form-actions">
                        <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cancelar</button>
                        <button type="submit" class="btn btn-primary"><i class="fas fa-save"></i> Inscribir Alumno</button>
                    </div>
                </form>
            </div>
        `;

        Utils.openModal('Añadir Alumno al Pre-Registro', modalHtml);
    },

    async handleAddStudent(event) {
        event.preventDefault();
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData);

        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/pre-registration', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify(data)
            });
            const resData = await res.json();
            if (resData.success) {
                Utils.showToast('Alumno inscrito con éxito', 'success');
                Utils.closeModal();
                await this.loadData();
                this.render();
            } else {
                SafeHTML.fire('Error', resData.error || 'No se pudo registrar', 'error');
            }
        } catch (e) {
            console.error(e);
            SafeHTML.fire('Error', 'Error de red al guardar alumno', 'error');
        }
    },

    showEditStudentModal(id) {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        const s = this.preRegistrations.find(item => item.id === id);
        if (!s) return;

        // Desplegable de la formación real del catálogo (no de los nombres de
        // grupo que ya hubiera en Prematrícula) — antes solo se podía elegir
        // entre grupos que YA tuvieran algún alumno dentro, así que un grupo
        // legítimo pero todavía vacío (p.ej. Interpretación 2º A/2ºB antes de
        // que nadie se le asigne) no aparecía como opción y no había forma de
        // sacar a un alumno de "Sin Asignar" hacia ahí. El nombre de grupo
        // (courseName, incluida la política de "Grado 1º") lo calcula el
        // servidor a partir de la formación elegida, no se manda a mano.
        const formations = (typeof FormationService !== 'undefined') ? FormationService.getFormations() : [];

        const modalHtml = `
            <div class="dnr-proposal pmv-form-dnr">
                <form id="edit-pre-student-form" ${UIEvents.attr("submit", function(event,captured){PreMatriculaView.handleUpdateStudent(event,String(captured[0]));}, [s.id])}>
                    <div class="form-group">
                        <label class="form-label">Formación *</label>
                        <select name="formationId" class="form-input" required>
                            <option value="" ${!s.formationId ? 'selected' : ''}>-- Sin asignar --</option>
                            ${formations.map(f => `<option value="${Utils.sanitizeHTML(f.id)}" ${s.formationId === f.id ? 'selected' : ''}>${Utils.sanitizeHTML(f.name)}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Curso Lectivo *</label>
                        <select name="academicYearStart" class="form-input" required>
                            ${this.renderYearOptionsForForm(s.academicYearStart)}
                        </select>
                    </div>
                    <div class="pmv-form-row-3">
                        <div class="form-group">
                            <label class="form-label">Nombre *</label>
                            <input type="text" name="firstName" value="${Utils.sanitizeHTML(s.firstName)}" class="form-input" required>
                        </div>
                        <div class="form-group">
                            <label class="form-label">1º Apellido *</label>
                            <input type="text" name="lastName1" value="${Utils.sanitizeHTML(s.lastName1)}" class="form-input" required>
                        </div>
                        <div class="form-group">
                            <label class="form-label">2º Apellido</label>
                            <input type="text" name="lastName2" value="${Utils.sanitizeHTML(s.lastName2)}" class="form-input">
                        </div>
                    </div>
                    <div class="pmv-form-row-3">
                        <div class="form-group">
                            <label class="form-label">Opción Pago *</label>
                            <select name="paymentOption" class="form-input" required>
                                <option value="C" ${s.paymentOption === 'C' ? 'selected' : ''}>C</option>
                                <option value="F" ${s.paymentOption === 'F' ? 'selected' : ''}>F</option>
                                <option value="FE" ${s.paymentOption === 'FE' ? 'selected' : ''}>FE</option>
                                ${!['C', 'F', 'FE'].includes(s.paymentOption) ? `<option value="${Utils.sanitizeHTML(s.paymentOption)}" selected>${Utils.sanitizeHTML(s.paymentOption)} (valor anterior)</option>` : ''}
                            </select>
                        </div>
                        <div class="form-group">
                            <label class="form-label">Precio Curso *</label>
                            <input type="number" step="0.01" name="coursePrice" value="${s.coursePrice}" class="form-input" required>
                        </div>
                        <div class="form-group">
                            <label class="form-label">Importe Pagado</label>
                            <input type="number" step="0.01" name="amountPaid" value="${s.amountPaid}" class="form-input" required>
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Estado Proceso *</label>
                        <select name="status" class="form-input" required>
                            ${Object.keys(this.statuses).map(k => `<option value="${k}" ${s.status === k ? 'selected' : ''}>${Utils.sanitizeHTML(this.statuses[k].label)}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Notas / Observaciones</label>
                        <textarea name="notes" class="form-input" rows="3">${Utils.sanitizeHTML(s.notes || '')}</textarea>
                    </div>
                    <div class="pmv-form-actions">
                        <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cancelar</button>
                        <button type="submit" class="btn btn-primary"><i class="fas fa-save"></i> Actualizar Alumno</button>
                    </div>
                </form>
            </div>
        `;

        Utils.openModal('Editar Ficha de Pre-Matrícula', modalHtml);
    },

    async handleUpdateStudent(event, id) {
        event.preventDefault();
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData);

        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch(`/api/pre-registration/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify(data)
            });
            const resData = await res.json();
            if (resData.success) {
                Utils.showToast('Datos de alumno actualizados', 'success');
                Utils.closeModal();
                await this.loadData();
                this.render();
            } else {
                SafeHTML.fire('Error', resData.error || 'No se pudo actualizar', 'error');
            }
        } catch (e) {
            console.error(e);
            SafeHTML.fire('Error', 'Error de red al actualizar alumno', 'error');
        }
    },

    deleteStudent(id, fullName) {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        Utils.showConfirm(
            `¿Estás seguro de que quieres eliminar a ${fullName} del pre-registro?`,
            async () => {
                try {
                    const token = AuthManager.getCsrfToken();
                    const res = await fetch(`/api/pre-registration/${id}`, {
                        method: 'DELETE',
                        headers: { 'X-SICAF-CSRF': token }
                    });
                    const resData = await res.json();
                    if (resData.success) {
                        Utils.showToast('Registro eliminado', 'success');
                        await this.loadData();
                        this.render();
                    } else {
                        SafeHTML.fire('Error', 'No se pudo eliminar el registro', 'error');
                    }
                } catch(e) {
                    console.error(e);
                    SafeHTML.fire('Error', 'Error de red al eliminar', 'error');
                }
            }
        );
    },

    // --- Agrupar formaciones (grupos de Pre-Matrícula) ---
    canManageGroups() {
        return AuthManager.canManageModule('pre-matricula', ['admin', 'coordinator', 'rrhh']);
    },

    // Plan + curso offered for grouping: active plans and their courses, by type.
    groupableMembers() {
        if (typeof StudyPlanService === 'undefined') return [];
        return StudyPlanService.getPlans({includeInactive: false})
            .sort((a, b) => a.order - b.order || String(a.name).localeCompare(String(b.name), 'es'))
            .flatMap(plan => StudyPlanService.years(plan.id, plan.category).map(year => ({
                studyPlanId: plan.id, academicYear: String(year.id), category: plan.category,
                label: `${plan.name} · ${year.name}`
            })));
    },

    memberLabel(member) {
        const plan = typeof StudyPlanService !== 'undefined' ? StudyPlanService.getPlan(member.studyPlanId) : null;
        const year = plan ? StudyPlanService.years(plan.id, plan.category).find(y => String(y.id) === String(member.academicYear)) : null;
        return `${plan?.name || member.studyPlanId} · ${year?.name || member.academicYear}`;
    },

    showSharedGroups() {
        const manage = this.canManageGroups();
        const rows = this.sharedGroups.map(group => `
            <li class="sp-row">
                <span class="sp-name">${Utils.sanitizeHTML(group.name)}
                    <span class="ft-years">${(group.members || []).map(m => `<span class="ft-year">${Utils.sanitizeHTML(this.memberLabel(m))}</span>`).join('')}</span>
                </span>
                <span class="sp-meta">Desde ${group.fromYear}/${group.fromYear + 1}</span>
                ${manage ? `<span class="sp-actions">
                    <button type="button" class="btn-icon" title="Editar" aria-label="Editar grupo" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.openSharedGroupForm(String(captured[0]));}, [group.id])}><i class="fas fa-edit"></i></button>
                    <button type="button" class="btn-icon" title="Eliminar" aria-label="Eliminar grupo" style="color: var(--dnr-danger);" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.deleteSharedGroup(String(captured[0]));}, [group.id])}><i class="fas fa-trash"></i></button>
                </span>` : ''}
            </li>`).join('');
        const content = `
            <div class="dnr-proposal sp-manager">
                <div class="sp-head">
                    <p class="sp-help">Desde el curso que indiques, las formaciones elegidas cuentan como un solo grupo económico en Pre-Matrícula (por ejemplo, el primer año de varias especialidades). Académicamente nada cambia: cada alumno sigue en su formación, y aquí se ve con la letra de su plan.</p>
                    ${manage ? `<button type="button" class="btn btn-primary btn-sm" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.openSharedGroupForm('');}, [])}><i class="fas fa-plus"></i> Nuevo grupo</button>` : ''}
                </div>
                ${rows ? `<ul class="sp-list">${rows}</ul>` : '<p class="sp-empty">No hay formaciones agrupadas.</p>'}
            </div>`;
        Utils.openModal('Agrupar formaciones', content);
    },

    openSharedGroupForm(id) {
        if (!this.canManageGroups()) return;
        const group = id ? this.sharedGroups.find(g => g.id === id) : null;
        if (id && !group) return;
        const chosen = new Set((group?.members || []).map(m => m.studyPlanId + '|' + m.academicYear));
        const members = this.groupableMembers();
        // A member of a deactivated plan stays listed while the group has it.
        (group?.members || []).forEach(m => {
            if (!members.some(x => x.studyPlanId === m.studyPlanId && x.academicYear === String(m.academicYear))) {
                members.push({studyPlanId: m.studyPlanId, academicYear: String(m.academicYear), category: '', label: this.memberLabel(m)});
            }
        });
        const byType = new Map();
        members.forEach(m => { if (!byType.has(m.category)) byType.set(m.category, []); byType.get(m.category).push(m); });
        const content = `
            <div class="dnr-proposal">
                <form id="shared-group-form" ${UIEvents.attr("submit", function(event,captured){PreMatriculaView.saveSharedGroup(event,String(captured[0]));}, [id || ''])}>
                    <div class="ft-form-grid">
                        <div class="form-group">
                            <label class="form-label" for="sg-name">Nombre del grupo *</label>
                            <input id="sg-name" name="name" class="form-input" required maxlength="80" value="${Utils.sanitizeHTML(group?.name || '')}" placeholder="Ej: Primer curso común">
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="sg-from">Desde el curso *</label>
                            <select id="sg-from" name="fromYear" class="form-select" required>
                                ${Array.from({length: 7}, (_, i) => this.todayAcademicYearStart() - 2 + i).concat(group ? [group.fromYear] : [])
                                    .filter((y, i, all) => all.indexOf(y) === i).sort((a, b) => a - b)
                                    .map(y => `<option value="${y}" ${y === (group?.fromYear ?? this.currentYearStart) ? 'selected' : ''}>${y}/${y + 1}</option>`).join('')}
                            </select>
                        </div>
                    </div>
                    <span class="form-label">Formaciones que se juntan * <small>(al menos dos)</small></span>
                    <div class="sg-members">
                        ${[...byType.entries()].map(([type, list]) => `
                            <fieldset class="sg-type">
                                <legend>${Utils.sanitizeHTML(type && typeof FormationTypeService !== 'undefined' ? FormationTypeService.plural(type) : 'Otros')}</legend>
                                ${list.map(m => `<label class="form-check"><input type="checkbox" name="member" value="${Utils.sanitizeHTML(m.studyPlanId + '|' + m.academicYear)}" ${chosen.has(m.studyPlanId + '|' + m.academicYear) ? 'checked' : ''}> ${Utils.sanitizeHTML(m.label)}</label>`).join('')}
                            </fieldset>`).join('') || '<p class="sp-empty">No hay planes de estudios activos.</p>'}
                    </div>
                    <div class="modal-sticky-actions">
                        <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){PreMatriculaView.showSharedGroups();}, [])}>Volver</button>
                        <button type="submit" class="btn btn-primary">${group ? 'Guardar cambios' : 'Crear grupo'}</button>
                    </div>
                </form>
            </div>`;
        Utils.openModal(group ? 'Editar grupo' : 'Nuevo grupo', content);
    },

    async sharedGroupRequest(method, id, data) {
        const response = await fetch('/api/pre-registration-groups' + (id ? '/' + encodeURIComponent(id) : ''), {
            method, headers: {'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken()},
            ...(data ? {body: JSON.stringify(data)} : {})
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo guardar el grupo.');
        return result;
    },

    async saveSharedGroup(event, id) {
        event.preventDefault();
        if (!this.canManageGroups() || this._savingGroup) return;
        const form = event.target;
        if (!form.checkValidity()) { form.reportValidity(); return; }
        const members = [...form.querySelectorAll('input[name="member"]:checked')].map(input => {
            const [studyPlanId, academicYear] = input.value.split('|');
            return {studyPlanId, academicYear};
        });
        if (members.length < 2) { Utils.showToast('Elige al menos dos formaciones.', 'error'); return; }
        this._savingGroup = true;
        try {
            await this.sharedGroupRequest(id ? 'PUT' : 'POST', id, {name: form.elements.name.value, fromYear: parseInt(form.elements.fromYear.value, 10), members});
            await this.loadData();
            Utils.showToast(id ? 'Grupo actualizado. Los alumnos ya apuntados conservan su grupo.' : 'Grupo creado.', 'success');
            this.render();
            this.showSharedGroups();
        } catch (error) {
            Utils.showToast(error.message, 'error');
        } finally {
            this._savingGroup = false;
        }
    },

    async deleteSharedGroup(id) {
        const group = this.sharedGroups.find(g => g.id === id);
        if (!group || !this.canManageGroups()) return;
        const answer = await SafeHTML.fire({
            icon: 'warning', title: '¿Eliminar el grupo?', text: `«${group.name}» dejará de juntar formaciones. Los alumnos ya apuntados conservan su grupo.`,
            showCancelButton: true, confirmButtonText: 'Eliminar', cancelButtonText: 'Cancelar'
        });
        if (!answer?.isConfirmed) return;
        try {
            await this.sharedGroupRequest('DELETE', id);
            await this.loadData();
            this.render();
            this.showSharedGroups();
        } catch (error) {
            Utils.showToast(error.message, 'error');
        }
    },

    showAddGroupModal() {
        SafeHTML.fire({
            title: `Nuevo Curso / Grupo · ${this.currentYearStart} / ${this.currentYearStart + 1}`,
            input: 'text',
            inputLabel: 'Nombre del Grupo de Curso',
            placeholder: 'Ej: DIRECCIÓN DE FOTOGRAFÍA 2º',
            showCancelButton: true,
            confirmButtonText: 'Crear Grupo',
            cancelButtonText: 'Cancelar',
            inputValidator: (value) => {
                if (!value) return 'El nombre del grupo es obligatorio';
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const groupName = result.value.trim().toUpperCase();

                // El grupo nuevo se crea dentro del curso lectivo que se
                // esté viendo en ese momento (ver selector de cabecera).
                const token = AuthManager.getCsrfToken();
                fetch('/api/pre-registration', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-SICAF-CSRF': token
                    },
                    body: JSON.stringify({
                        courseName: groupName,
                        academicYearStart: this.currentYearStart,
                        firstName: 'PLAZA',
                        lastName1: 'PENDIENTE',
                        paymentOption: 'C',
                        coursePrice: 0,
                        amountPaid: 0,
                        status: 'entrevista_solicitada',
                        notes: 'Registro inicial de grupo'
                    })
                }).then(res => res.json()).then(data => {
                    if (data.success) {
                        Utils.showToast(`Grupo '${groupName}' creado correctamente`, 'success');
                        this.loadData().then(() => this.render());
                    }
                });
            }
        });
    },

    // Proyecta en bloque a los alumnos activos del curso actual de Alumnado como
    // candidatos ("Previsión") al curso que se esté viendo aquí +1. No toca a
    // quien ya tenga registro para ese curso destino (server.js se encarga de
    // no duplicar/pisar). Ver plan: cozy-leaping-wilkinson.
    generatePromotionForecast() {
        if (!AuthManager.requireModuleManage("pre-matricula",["admin","coordinator","rrhh"])) return;
        const targetYear = this.currentYearStart;
        SafeHTML.fire({
            title: `¿Generar previsión de promoción?`,
            html: `Se proyectará a los alumnos activos del curso actual de Alumnado como candidatos al curso <b>${targetYear} / ${targetYear + 1}</b>, con la formación destino autosugerida (editable después). Esto puede crear o actualizar varios registros a la vez.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#0d9488',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, generar previsión',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                SafeHTML.fire({ title: 'Generando previsión…', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                const token = AuthManager.getCsrfToken();
                const response = await fetch('/api/pre-registration/generate-forecast', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-SICAF-CSRF': token
                    },
                    body: JSON.stringify({ targetAcademicYearStart: targetYear })
                });
                const data = await response.json();
                if (!data.success) throw new Error(data.error || 'Error del servidor');

                await this.loadData();
                this.render();
                SafeHTML.fire('Previsión generada', `${data.created} candidato(s) nuevo(s) proyectado(s).${data.skipped ? ` ${data.skipped} ya tenían registro y no se han tocado.` : ''}`, 'success');
            } catch (err) {
                SafeHTML.fire('Error', err.message, 'error');
            }
        });
    }
};

window.PreMatriculaView = PreMatriculaView;
